//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useContext } from 'react';
import { Grid, makeStyles } from '@material-ui/core';
import BookmarkBorderIcon from '@material-ui/icons/BookmarkBorder';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import SchoolIcon from '@material-ui/icons/School';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import MessageIcon from '@material-ui/icons/Message';
import FacebookIcon from '@material-ui/icons/Facebook';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import InstagramIcon from '@material-ui/icons/Instagram';
import YouTubeIcon from '@material-ui/icons/YouTube';
import WebsiteIcon from '@material-ui/icons/Language';
import GitHubIcon from '@material-ui/icons/GitHub';
import { Bookmark } from '@material-ui/icons';
import SimpleBar from 'simplebar-react';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { logEvent } from '../../system/firebase';
// import { unsubscribeRealtimeUpdates } from '../../system/firebase';
import { LayoutContext, AppContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { getSalaryRateText, getNameByUser, getCityStateCountryText, getFirstOccupationText, getYearsOfExperienceText } from '../../common/textHelper';
import { saveJobSeekerToJobs } from '../../common/services/empDashboardServices';
// import { loadActiveJobsListByEmployerId } from '../../common/services/empMyJobsServices';

import {
    PROFILE_SOCIAL_MEDIA_FACEBOOK,
    PROFILE_SOCIAL_MEDIA_INSTAGRAM,
    PROFILE_SOCIAL_MEDIA_LINKEDIN,
    PROFILE_SOCIAL_MEDIA_WEBSITE,
    PROFILE_SOCIAL_MEDIA_YOUTUBE,
    TITLE_MISSING,
    PROFILE_SOCIAL_MEDIA_TIKTOK,
    PROFILE_SOCIAL_MEDIA_GITHUB,
    CONFIRM_DIALOG_TITLE_CREATE_NEW_JOB_FOR_INTERVIEW_OR_MESSAGE,
    CONFIRM_DIALOG_MESSAGE_CREATE_NEW_JOB_FOR_INTERVIEW_OR_MESSAGE,
    BASE_URL,
    DASHBOARD_ID_MY_JOBS,
    ANALYTICS_EVENT_EMP_SAVED_TALENT,
    PROFILE_SOCIAL_MEDIA_X,
} from '../../common/constants';
import { PATH_DASHBOARD } from '../../system/constants';
import { getFormattedDate } from '../../common/dateHelper';
import { getVideoOverviewDurationTextBySeconds } from '../../common/videoHelper';
// import { fetchEmployerActiveJobs, loadConversations } from '../../common/services/talentCenterServices';
import { getUniqueKey } from '../../common/arrayHelper';
import { convertFileSize, getFileTypeLabelsByFilename } from '../../common/fileHelper';
import { getUserDateFormat } from '../../common/dateHelper';
import TiktokIcon from '../../assets/tiktok_icon.png';
import XIcon from '../../assets/x_logo_black.png';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdFileTypeIcon from '../../common/elemental/AfitdFileTypeIcon';
import VideoOverviewBlock from '../../dashboardMyProfile/subComponents/VideoOverviewBlock';
import AfitdLink from '../../common/elemental/AfitdLink';
import AfitdAvatarWithRing from '../../common/elemental/AfitdAvatarWithRing';
import AfitdAvatar from '../../common/elemental/AfitdAvatar';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import InterviewDialog from './InterviewDialog';
import SendMessageDialog from './SendMessageDialog';
import SaveJobSeekerToMyJobsPopover from './SaveJobSeekerToMyJobsPopover';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import cx from 'clsx';
// import { format } from 'date-fns';
import _get from 'lodash/get';
import _map from 'lodash/map';
import _find from 'lodash/find';
import _isEmpty from 'lodash/isEmpty';
import _filter from 'lodash/filter';
import _size from 'lodash/size';
import _omit from 'lodash/omit';
import _some from 'lodash/some';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const { userProfileInfo } = params;

    const socialMediaBlockUrlStyles = {
        fontFamily: 'Roboto, sans-serif',
        fontSize: '14px',
        lineHeight: '16px',
        // color: '#9B9E9C',
        // color: `${theme.palette.secondary.main}`,
        color: '#1D8A8B',
        fontWeight: '500',
        padding: '0 0 0 15px',
        // margin: '13px 0 0 0',
    };

    const socialMediaBlockUrlPlaceHolderStyles = {
        fontFamily: 'Roboto, sans-serif',
        fontSize: '14px',
        lineHeight: '16px',
        color: 'rgba(17, 30, 20, 0.4)',
        fontWeight: '500',
        padding: '5px 21px 0px 15px',
    };

    // const facebookUrlStyles = _isEmpty(userProfileInfo?.facebookUrl) ? socialMediaBlockUrlPlaceHolderStyles : socialMediaBlockUrlStyles;
    // const linkedInUrlStyles = _isEmpty(userProfileInfo?.linkedInUrl) ? socialMediaBlockUrlPlaceHolderStyles : socialMediaBlockUrlStyles;
    // const youTubeUrlStyles = _isEmpty(userProfileInfo?.youTubeUrl) ? socialMediaBlockUrlPlaceHolderStyles : socialMediaBlockUrlStyles;
    // const instagramUrlStyles = _isEmpty(userProfileInfo?.instagramUrl) ? socialMediaBlockUrlPlaceHolderStyles : socialMediaBlockUrlStyles;
    const xUrlStyles = _isEmpty(userProfileInfo?.twitterUrl) ? { ...socialMediaBlockUrlPlaceHolderStyles, paddingLeft: 22 } : { ...socialMediaBlockUrlStyles, paddingLeft: 22 };
    // const websiteUrlStyles = _isEmpty(userProfileInfo?.websiteUrl) ? socialMediaBlockUrlPlaceHolderStyles : socialMediaBlockUrlStyles;
    const tikTokUrlStyles = _isEmpty(userProfileInfo?.tikTokUrl) ? { ...socialMediaBlockUrlPlaceHolderStyles, paddingLeft: 22 } : { ...socialMediaBlockUrlStyles, paddingLeft: 22 };
    // const gitHubUrlStyles = _isEmpty(userProfileInfo?.gitHubUrl) ? socialMediaBlockUrlPlaceHolderStyles : socialMediaBlockUrlStyles;

    const socialMediaBlockTitleStyles = {
        fontFamily: 'Roboto, sans-serif',
        fontSize: '16px',
        lineHeight: '19px',
        color: '#111E14',
        fontWeight: '600',
        padding: '15px 21px 0px 15px',
    };

    return makeStyles(theme => {
        const titleStyles = { fontWeight: 'bold', margin: '25px 0 10px 0', [theme.breakpoints.down('sm')]: { margin: '0px 15px' } };

        return {
            closeButton: { margin: '20px 36px 20px 20px' },
            root: {
                backgroundColor: 'white',
                overflow: 'hidden',
                height: '100%',
                borderRadius: 10,
                paddingRight: 16,
                [theme.breakpoints.down('sm')]: { paddingLeft: 6, paddingRight: 6 },
                // [theme.breakpoints.down('lg')]: { width: '100%' },
            },
            inDialog: {
                height: 'calc(100vh - 200px)',
            },
            mainGrid: {
                height: '100%',
            },
            dpGrid: {
                display: 'flex',
                justifyContent: 'center',
                [theme.breakpoints.up('md')]: { marginTop: 16 },
                [theme.breakpoints.down('sm')]: { paddingRight: 4, justifyContent: 'flex-end' },
            },
            profilePic: {},
            contentGrid: {
                height: '100%',
                overflowY: 'hidden',
                display: 'flex',
                flexDirection: 'column',
            },
            content: {
                // padding: '20px 40px',
                // height: '58vh',
                // height: '200px',
                // overflowY: 'scroll',
                // '&::-webkit-scrollbar-track': { borderRadius: '10px', background: 'none' },
                // '&::-webkit-scrollbar': { width: '8px', backgroundColor: '#F5F5F5' },
                // '&::-webkit-scrollbar-thumb': {
                //     borderRadius: '10px',
                //     backgroundColor: 'rgba(0,0,0,0.2)',
                // },
                overflowY: 'hidden',
            },
            headingSection: { display: 'flex', alignItems: 'center' },
            nameText: { fontWeight: 'bold', flex: 1 },
            subInfo: {
                color: '#1D8A8B',
                textTransform: 'uppercase',
                fontWeight: 'bold',
                marginRight: 15,
            },
            detailContainer: {
                flexDirection: 'row',
                display: 'flex',
                alignItems: 'center',
                marginRight: 20,
            },
            address: { fontWeight: '500', margin: '20px 0' },
            detail: { fontWeight: 'bold' },
            title: titleStyles,

            willingRelocateTitleText: { ...titleStyles },
            willingRelocateText: {
                background: '#1D8A8B',
                marginRight: 10,
                padding: '5px 20px',
                borderRadius: 4,
                color: 'white',
                fontWeight: '400',
            },

            yearsOfExperienceTitleText: { ...titleStyles },
            aboutMeTitleText: { ...titleStyles },
            occupationTitleText: { ...titleStyles },
            skillsetTitleText: { ...titleStyles },
            videoOverviewTitleText: { ...titleStyles },
            resumeBlockTitleText: { ...titleStyles },
            socialMediaPortfolioBlockTitleText: { ...titleStyles },

            icons: { color: '#9B9E9C', fontSize: 16, marginRight: 10 },
            subInfoContainer: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            },
            miscInfoContainer: {
                display: 'flex',
                // overflowX: 'scroll',
                flexDirection: 'row',
                flexWrap: 'wrap',
                [theme.breakpoints.down('sm')]: { margin: '0px 15px' },
            },
            descriptionText: { fontWeight: 400, [theme.breakpoints.down('sm')]: { margin: '0px 15px' } },
            occupations: {
                background: '#1D8A8B',
                marginRight: 10,
                marginBottom: 8,
                padding: '5px 10px',
                borderRadius: 4,
                color: 'white',
                fontWeight: '400',
            },
            skills: {
                background: '#F0F2F6',
                marginRight: 10,
                marginBottom: 8,
                padding: '5px 10px',
                borderRadius: 4,
                fontWeight: '400',
            },
            chipsContainer: { display: 'flex', flexWrap: 'wrap', [theme.breakpoints.down('sm')]: { margin: '0px 15px' } },
            innerContainer: {
                padding: 20,
                border: '1px solid #D5D7DD',
                borderRadius: 4,
                marginTop: 10,
                background: 'rgba(255,255,255,0.9)',
            },
            inline: { display: 'flex', alignItems: 'center' },
            employerActionsContainer: {
                display: 'flex',
                justifyContent: 'space-between',
                padding: '30px 0',
                borderBottom: '1px solid #F0F2F6',
                [theme.breakpoints.down('sm')]: { justifyContent: 'center' },
            },
            interviewButton: { marginRight: 15, [theme.breakpoints.down('sm')]: { width: '100%', marginBottom: 16 } },
            messageButton: { [theme.breakpoints.down('sm')]: { width: '100%', marginBottom: 16 } },
            saveToJobsButton: { [theme.breakpoints.down('sm')]: { width: '100%' } },

            resumeLink: { textDecoration: 'none' },
            fileTextContainer: { marginLeft: 20 },

            rootDialog: { paddingTop: '0 !important', padding: '0 24px !important' },

            metaContainer: {
                position: 'sticky',
                background: 'white',
                top: 0,
                paddingTop: 20,
                zIndex: 5,
                [theme.breakpoints.down('sm')]: { display: 'flex', margin: '0px 15px' },
                // [theme.breakpoints.up('md')]: { display: 'flex', flexDirection: 'column-reverse', alignItems: 'flex-start' },
            },
            otherEducation: {
                display: 'flex',
                marginLeft: '5px',
                marginRight: '5px',
                alignItems: 'center',
                justifyContent: 'center',
            },

            existingVideoInfoContainer: {
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
            },
            existingVideoNameSizeContainer: {
                width: '100%',
                display: 'flex',
            },
            existingVideoFileSizeText: {
                color: '#9B9E9C',
                marginLeft: '5px',
            },
            existingVideoDurationText: {
                color: '#9B9E9C',
            },

            socialMediaBlock: {
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                borderRadius: '4px',
                margin: '13px 0 0 0',
                padding: '0 0 10px 0',
                border: '1px solid #D5D7DD',
            },
            socialIcons: {
                // fontSize: '46px',
                // padding: '12px',
                // paddingBottom: '0px',
                // [theme.breakpoints.down('md')]: { padding: '3px', marginTop: 7 },
                fontSize: '46px',
                padding: '0px',
                paddingTop: 8,
                margin: '0px 8px',
            },
            socialMediaBlockTitle: socialMediaBlockTitleStyles,
            socialMediaBlockTitleTikTok: { ...socialMediaBlockTitleStyles, paddingLeft: 20 },
            socialMediaBlockTitleX: { ...socialMediaBlockTitleStyles, paddingLeft: 22 },
            socialMediaBlockUrl: socialMediaBlockUrlStyles,
            socialMediaBlockUrlPlaceHolder: socialMediaBlockUrlPlaceHolderStyles,
            xUrl: { ...xUrlStyles },
            tikTokUrl: tikTokUrlStyles,

            hidden: {
                display: 'none',
            },
            iconsContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
        };
    });
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        props,
        classes,
        smMatches,
        userDateFormat,
        // employerId,
        activeJobsCount,
        userProfileInfo = {},
        setInterviewDialogOpen,
        interviewDialogOpen,
        setSendMessageDialogOpen,
        sendMessageDialogOpen,
        handleSaveToJobs,
        // conversations,
        popoverId,
        popoverOpen,
        anchorEl,
        setAnchorEl,
        setPopoverOpen,
        handleSaveJobSeekerToMyJobs,
        isJobSeekerAlreadySaved,
        noActiveJobsDialogOpen,
        setNoActiveJobsDialogOpen,
        handleConfirmActiveJobDialog,
        clickedOn,
        setClickedOn,
        hideThumbnail,
        setHideThumbnail,
    } = params;

    const { onClose, jobSeeker, isInDialog, isShowEmployerActions } = props;
    // console.log({ jsInDets: jobSeeker });

    const {
        root: rootCls,
        inDialog: inDialogCls,
        innerContainer: innerContainerCls,
        employerActionsContainer: employerActionsContainerCls,
        socialMediaBlockUrl: socialMediaBlockUrlCls,
        socialMediaBlockUrlPlaceHolder: socialMediaBlockUrlPlaceHolderCls,
        hidden: hiddenCls,
    } = classes;

    const jobSeekerId = _get(jobSeeker, 'id');
    const jobSeekerName = getNameByUser(_get(jobSeeker, 'data'));
    const jobSeekerProfilePicUrl = _get(jobSeeker, 'data.profilePicUrl');

    const willingRelocate = _get(jobSeeker, 'data.willingRelocate');

    const existingVideoFilenameText = _get(jobSeeker, 'data.videoOverviewName');

    const videoOverviewSize = _get(jobSeeker, 'data.videoOverviewSize');
    const { size: videoOverviewSizeText, unit: videoOverviewUnitText } = convertFileSize(videoOverviewSize);
    const existingVideoFileSizeText = `${videoOverviewSizeText}${videoOverviewUnitText}`;

    const videoOverviewDuration = _get(jobSeeker, 'data.videoOverviewDuration');
    const existingVideoDurationText = getVideoOverviewDurationTextBySeconds(videoOverviewDuration);

    const videoOverviewUrl = _get(jobSeeker, 'data.videoOverviewUrl', '');

    const resumeList = _get(jobSeeker, 'data.resumeList', []);
    const resumeContainerHidden = resumeList.length === 0;
    const activeResume = _find(resumeList, o => o.isActive);
    const resumeTitle = _get(activeResume, 'title', TITLE_MISSING);
    const resumeFileUploadDateText = getFormattedDate(_get(activeResume, 'updateDt'), userDateFormat);
    const resumeFileUrl = _get(activeResume, 'attachment.url');
    const resumeFileTitle = _get(activeResume, 'attachment.title');
    const { label: resumeFileTypeLabel /*labelLong: resumeFileTypeLabelLong*/ } = getFileTypeLabelsByFilename(resumeFileTitle);

    const isSocialMediaLinkExists = _some(
        ['facebookUrl', 'linkedInUrl', 'youTubeUrl', 'instagramUrl', 'twitterUrl', 'tikTokUrl', 'gitHubUrl'],
        fieldName => !!userProfileInfo[fieldName],
    );

    const defns = {
        root: { className: cx(rootCls, { [inDialogCls]: isInDialog }) },
        closeButton: { onClick: onClose },
        nameText: { variant: 'h6', value: jobSeekerName },
        subInfo: { variant: 'caption' },
        address: { variant: 'body1' },
        title: { variant: 'body1' },
        detail: { variant: 'body2' },
        descriptionText: { variant: 'body2' },

        willingRelocate: { data: willingRelocate },

        yearsOfExperienceTitleText: { isHidden: !userProfileInfo?.yearsOfExperience && parseInt(userProfileInfo?.yearsOfExperience) !== 0 },
        aboutMeTitleText: { isHidden: !userProfileInfo?.aboutMe },
        occupationTitleText: { isHidden: _size(userProfileInfo?.occupation) === 0 },
        skillsetTitleText: { isHidden: _size(userProfileInfo?.skillset) === 0 },
        videoOverviewTitleText: { isHidden: !videoOverviewUrl },
        resumeBlockTitleText: { isHidden: resumeContainerHidden },
        socialMediaPortfolioBlockTitleText: { isHidden: !isSocialMediaLinkExists },

        occupations: { variant: 'body2' },
        skills: { variant: 'body2' },

        resumeContainer: { className: cx(innerContainerCls, { [hiddenCls]: resumeContainerHidden }) },
        resumeLink: { target: '_blank', rel: 'noopener noreferrer', href: !!resumeFileUrl ? resumeFileUrl : undefined },
        resumeFileTypeIcon: { label: resumeFileTypeLabel },
        resumeTitleText: { variant: 'body2', value: resumeTitle },
        resumeDateText: { variant: 'body2', value: resumeFileUploadDateText },

        mainGrid: { container: true, spacing: 2 },
        dpGrid: { item: true, lg: 2, md: 2, sm: 12, xs: 12 },
        profilePic: {
            photoSize: smMatches ? 50 : 92,
            borderSize: smMatches ? 3 : 4,
            placeholderFontSize: smMatches ? 20 : 30,
            src: userProfileInfo?.profilePicUrl,
            alt: 'Profile Picture',
            // [theme.breakpoints.down('sm')]: { width: 50, height: 50, marginLeft: '-1px', marginTop: '-1px' },
        },
        contentGrid: { item: true, lg: 10, md: 10, sm: 12, xs: 12 },
        content: {},
        employerActionsContainer: {
            className: cx(employerActionsContainerCls, { [hiddenCls]: !isShowEmployerActions }),
            container: true,
        },
        interviewButton: {
            onClick: () => {
                if (activeJobsCount > 0) setInterviewDialogOpen(true);
                else {
                    setClickedOn('interview');
                    setNoActiveJobsDialogOpen(true);
                }
            },
            color: 'primary',
            startIcon: <CalendarTodayIcon />,
        },
        messageButton: {
            onClick: () => {
                if (activeJobsCount > 0) setSendMessageDialogOpen(true);
                else {
                    setClickedOn('message');
                    setNoActiveJobsDialogOpen(true);
                }
            },
            color: 'secondary',
            startIcon: <MessageIcon />,
        },
        saveToJobsButton: {
            onClick: e => {
                if (activeJobsCount > 0) handleSaveToJobs(e);
                else {
                    setClickedOn('saveToJobs');
                    setNoActiveJobsDialogOpen(true);
                }
            },
            startIcon: isJobSeekerAlreadySaved ? <Bookmark color="primary" /> : <BookmarkBorderIcon />,
            'aria-describedby': popoverId,
        },
        interviewDialog: {
            onClose: () => setInterviewDialogOpen(false),
            open: interviewDialogOpen,
            limitExceeded: false,
            jobSeekerId,
            jobSeekerName,
            jobSeekerProfilePicUrl,
        },
        sendMessageDialog: {
            onClose: () => setSendMessageDialogOpen(false),
            open: sendMessageDialogOpen,
            // conversations,
            jobSeekerId,
            jobSeekerName,
            jobSeekerProfilePicUrl,
        },
        saveJobSeekerToMyJobsPopover: {
            popoverOpen,
            anchorEl,
            setAnchorEl,
            id: popoverId,
            setPopoverOpen,
            onSave: handleSaveJobSeekerToMyJobs,
            jobSeeker,
        },
        innerContainerVideoOverview: {
            className: cx(innerContainerCls, { [hiddenCls]: !videoOverviewUrl }),
        },
        existingVideoFilenameText: {
            variant: 'body1',
            value: existingVideoFilenameText,
        },
        existingVideoFileSizeText: {
            variant: 'body2',
            value: existingVideoFileSizeText,
        },
        existingVideoDurationText: {
            value: existingVideoDurationText,
        },
        videoOverviewBlock: {
            videoOverviewUrl,
            onPlayVideo: () => setHideThumbnail(true),
            hideThumbnail,
        },

        facebookUrl: {
            className: _isEmpty(userProfileInfo.facebookUrl) ? socialMediaBlockUrlPlaceHolderCls : socialMediaBlockUrlCls,
            value: 'Not Connected',
            label: userProfileInfo.facebookUrl,
            onClick: handleClickUrlLink.bind(null, userProfileInfo.facebookUrl),
        },
        linkedInUrl: {
            className: _isEmpty(userProfileInfo.linkedInUrl) ? socialMediaBlockUrlPlaceHolderCls : socialMediaBlockUrlCls,
            value: 'Not Connected',
            label: userProfileInfo.linkedInUrl,
            onClick: handleClickUrlLink.bind(null, userProfileInfo.linkedInUrl),
        },
        youTubeUrl: {
            className: _isEmpty(userProfileInfo.youTubeUrl) ? socialMediaBlockUrlPlaceHolderCls : socialMediaBlockUrlCls,
            value: 'Not Connected',
            label: userProfileInfo.youTubeUrl,
            onClick: handleClickUrlLink.bind(null, userProfileInfo.youTubeUrl),
        },
        instagramUrl: {
            className: _isEmpty(userProfileInfo.instagramUrl) ? socialMediaBlockUrlPlaceHolderCls : socialMediaBlockUrlCls,
            value: 'Not Connected',
            label: userProfileInfo.instagramUrl,
            onClick: handleClickUrlLink.bind(null, userProfileInfo.instagramUrl),
        },
        xUrl: {
            // className: _isEmpty(userProfileInfo.twitterUrl) ? socialMediaBlockUrlPlaceHolderCls : socialMediaBlockUrlCls,
            value: 'Not Connected',
            label: userProfileInfo.twitterUrl,
            onClick: handleClickUrlLink.bind(null, userProfileInfo.twitterUrl),
        },
        websiteUrl: {
            className: _isEmpty(userProfileInfo.websiteUrl) ? socialMediaBlockUrlPlaceHolderCls : socialMediaBlockUrlCls,
            value: 'Not Connected',
            label: userProfileInfo.websiteUrl,
            onClick: handleClickUrlLink.bind(null, userProfileInfo.websiteUrl),
        },
        tikTokUrl: {
            // className: _isEmpty(userProfileInfo.tikTokUrl) ? socialMediaBlockUrlPlaceHolderCls : socialMediaBlockUrlCls,
            value: 'Not Connected',
            label: userProfileInfo.tikTokUrl,
            onClick: handleClickUrlLink.bind(null, userProfileInfo.tikTokUrl),
        },
        gitHubUrl: {
            className: _isEmpty(userProfileInfo.gitHubUrl) ? socialMediaBlockUrlPlaceHolderCls : socialMediaBlockUrlCls,
            value: 'Not Connected',
            label: userProfileInfo.gitHubUrl,
            onClick: handleClickUrlLink.bind(null, userProfileInfo.gitHubUrl),
        },
        noActiveJobsDialog: {
            title: CONFIRM_DIALOG_TITLE_CREATE_NEW_JOB_FOR_INTERVIEW_OR_MESSAGE,
            onClose: () => setNoActiveJobsDialogOpen(false),
            onConfirm: handleConfirmActiveJobDialog,
            open: noActiveJobsDialogOpen,
            message: `${CONFIRM_DIALOG_MESSAGE_CREATE_NEW_JOB_FOR_INTERVIEW_OR_MESSAGE} ${
                clickedOn === 'message' ? 'send message' : clickedOn === 'interview' ? 'request an interview' : clickedOn === 'saveToJobs' ? 'save to My Jobs' : ''
            }`,
            yesButtonLabel: 'Create Active Job',
            noButtonLabel: 'Cancel',
            yesButtonColor: 'primary',
        },
    };

    setClassNameInDefns(defns, classes);
    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const UrlLink = props => {
    if (_isEmpty(props.label)) {
        return <AfitdTypography {...props} />;
    } else {
        return <AfitdLink {...props} rel="noopener noreferrer" />;
    }
};

const handleClickUrlLink = (url, evt) => {
    if (!_isEmpty(url)) {
        window.open(url, '_blank');
    }
};

const WillingToRelocate = props => {
    const { data: willingRelocate } = props;
    const classes = useStyles(/*{param1, param2}*/)();

    let defns = { willingRelocateTitleText: { isHidden: !willingRelocate }, willingRelocateText: { variant: 'body2' } };
    setClassNameInDefns(defns, classes);

    if (!willingRelocate) {
        return null;
    } else {
        return (
            <>
                <AfitdTypography {...defns.willingRelocateTitleText}>Willing to Relocate?</AfitdTypography>
                <div {...defns.chipsContainer}>
                    <AfitdTypography {...defns.willingRelocateText}>YES</AfitdTypography>
                </div>
            </>
        );
    }
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const JobSeekerProfile = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const employerId = _get(appContext, 'employerId');
    // const employerData = _get(appContext, 'currentEmployer');
    const layoutContext = useContext(LayoutContext);
    const { dashboardData, setRequestRefreshSavedToJobs, setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;
    const userDateFormat = getUserDateFormat(appContext);
    const theme = useTheme();
    const mdMatchesOnly = useMediaQuery(theme.breakpoints.only('md'));
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [interviewDialogOpen, setInterviewDialogOpen] = useState(false);
    const [sendMessageDialogOpen, setSendMessageDialogOpen] = useState(false);
    // const [conversations, setConversations] = useState([]); // Commented out as we don't need 'send to' Autocomplete
    const [anchorEl, setAnchorEl] = useState(null);
    const [popoverOpen, setPopoverOpen] = useState(Boolean(anchorEl));
    const [noActiveJobsDialogOpen, setNoActiveJobsDialogOpen] = useState(false);
    const [clickedOn, setClickedOn] = useState('null');
    const [hideThumbnail, setHideThumbnail] = useState(false);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { jobSeeker, jobSeekerApplySavedDt, isSaved, setSelected } = props;

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const userFormat = 'MM/d/yyyy';
    const popoverId = popoverOpen ? 'simple-popover' : undefined;
    const activeJobsCount = dashboardData?.activeJobsCount;
    const jobSeekerId = _get(jobSeeker, 'id');
    const isJobSeekerAlreadySaved = _size(_get(jobSeeker, 'data.savedToJobs')) > 0;
    const userProfileInfo = _get(jobSeeker, 'data'); //TODO refactor to use defns

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // useEffect(() => {
    //     if (bookmarkedJob.length > 0) {
    //         const jobSeekerData = {
    //             email: jobSeeker.data.email,
    //             firstName: jobSeeker.data.firstName,
    //             lastName: jobSeeker.data.lastName,
    //             profilePicUrl: jobSeeker.data.profilePicUrl,
    //             userId: jobSeeker.id,
    //         };
    //         bookmarkedJob.forEach(element => {
    //             saveJobSeekerinJobsDoc(employerId, jobSeekerData, element.uid);
    //         });
    //     }

    //     console.log({ bookmarkedJob });
    //     //eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [bookmarkedJob.length]);

    // We don't need to load these data for now -el 20220503
    // useEffect(() => {
    //     if (employerId) {
    //         const unsubscribeLoadConversations = loadConversations(employerId, setConversations);
    //         const unsubscribeFetchEmployerActiveJobs = fetchEmployerActiveJobs(setJobListings, employerId);
    //         return () => {
    //             unsubscribeRealtimeUpdates([unsubscribeLoadConversations, unsubscribeFetchEmployerActiveJobs]);
    //         };
    //     }
    // }, [employerId]);

    //---------------------------------------------------------------------
    // Event handlers
    //---------------------------------------------------------------------
    const handleSaveToJobs = event => {
        setAnchorEl(event.currentTarget);
        setPopoverOpen(true);
    };

    const handleConfirmActiveJobDialog = () => {
        setNoActiveJobsDialogOpen(false);
        window.open(`${BASE_URL}${PATH_DASHBOARD}/${DASHBOARD_ID_MY_JOBS}/addJob`, '_blank').focus();
    };

    const handleSaveJobSeekerToMyJobs = async saveToJobs => {
        const jobSeekerData = {
            email: jobSeeker.data.email,
            firstName: jobSeeker.data.firstName,
            lastName: jobSeeker.data.lastName,
            profilePicUrl: jobSeeker.data.profilePicUrl,
            userId: jobSeeker.id,
        };

        const jobsToSave = _filter(saveToJobs, job => job.isNew);
        const jobsToUnSave = _filter(saveToJobs, job => job.isDelete);

        await saveJobSeekerToJobs({
            employerId,
            jobSeekerData,
            jobsToSave,
            jobsToUnSave,
            callback: () => {
                // close popover
                setPopoverOpen(false);
                // update local states savedToJobs because ElasticSearch won't auto refresh
                setRequestRefreshSavedToJobs({ jobSeekerId, newSavedToJobs: saveToJobs });
                setSelected(prev => {
                    const savedToJobs = _map(
                        _filter(saveToJobs, job => !job.isDelete),
                        job => _omit(job, ['isNew']),
                    );
                    const data = _get(jobSeeker, 'data');
                    return { ...jobSeeker, data: { ...data, savedToJobs } };
                });
                logEvent(ANALYTICS_EVENT_EMP_SAVED_TALENT);
            },
            setSnackbarMessage,
            setSnackbarErrorMessage,
        });
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const classes = useStyles({ userProfileInfo })();
    const defns = getDefns({
        props,
        classes,
        smMatches,
        userDateFormat,
        employerId,
        activeJobsCount,
        userProfileInfo,
        interviewDialogOpen,
        setInterviewDialogOpen,
        setSendMessageDialogOpen,
        handleSaveToJobs,
        sendMessageDialogOpen,
        // conversations,
        popoverId,
        popoverOpen,
        anchorEl,
        setAnchorEl,
        setPopoverOpen,
        handleSaveJobSeekerToMyJobs,
        isJobSeekerAlreadySaved,
        noActiveJobsDialogOpen,
        setNoActiveJobsDialogOpen,
        handleConfirmActiveJobDialog,
        clickedOn,
        setClickedOn,
        hideThumbnail,
        setHideThumbnail,
    });
    // console.log('userProfileInfo?.payFrequency', userProfileInfo?.payFrequency);

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return !jobSeeker ? (
        <div style={{ padding: '40px' }}>No Selection</div>
    ) : (
        <div {...defns.root}>
            <Grid {...defns.mainGrid}>
                {smMatches ? null : (
                    <Grid {...defns.dpGrid}>{userProfileInfo?.profilePicUrl ? <AfitdAvatarWithRing {...defns.profilePic} /> : <AfitdAvatar {...defns.profilePic} />}</Grid>
                )}
                <Grid {...defns.contentGrid}>
                    <div {...defns.metaContainer}>
                        <div>
                            <div {...defns.headingSection}>
                                <AfitdTypography {...defns.nameText} />
                                <div {...defns.subInfoContainer}>
                                    {jobSeekerApplySavedDt && (
                                        <AfitdTypography {...defns.subInfo}>
                                            <i>{isSaved ? 'Saved on' : 'Applied on'}</i>: {getFormattedDate(jobSeekerApplySavedDt, userFormat)}
                                        </AfitdTypography>
                                    )}
                                </div>
                            </div>

                            <AfitdTypography {...defns.address}>
                                {getFirstOccupationText(userProfileInfo.occupation, userProfileInfo.resumeList)} -{' '}
                                {getCityStateCountryText(userProfileInfo?.city, userProfileInfo?.state, userProfileInfo?.country)}
                            </AfitdTypography>
                        </div>

                        {smMatches && (
                            <Grid {...defns.dpGrid}>{userProfileInfo?.profilePicUrl ? <AfitdAvatarWithRing {...defns.profilePic} /> : <AfitdAvatar {...defns.profilePic} />}</Grid>
                        )}
                    </div>
                    <div {...defns.content}>
                        <SimpleBar style={{ height: '100%' }}>
                            <div {...defns.miscInfoContainer}>
                                <div {...defns.detailContainer}>
                                    <AfitdTypography {...defns.icons}>{userProfileInfo?.currencySymbol} </AfitdTypography>
                                    <AfitdTypography {...defns.detail}>
                                        {' '}
                                        {getSalaryRateText(
                                            userProfileInfo?.isExactRate,
                                            userProfileInfo?.rateMin,
                                            userProfileInfo?.rateMax,
                                            userProfileInfo?.currency,
                                            userProfileInfo?.payFrequency,
                                        )}{' '}
                                    </AfitdTypography>
                                </div>
                                {userProfileInfo?.employmentType?.length < 0 ? (
                                    <AccessTimeIcon {...defns.icons} />
                                ) : (
                                    userProfileInfo?.employmentType?.map((type, index) => (
                                        <div {...defns.detailContainer} key={getUniqueKey()}>
                                            <AccessTimeIcon {...defns.icons} />
                                            <AfitdTypography {...defns.detail}>{type}</AfitdTypography>
                                        </div>
                                    ))
                                )}
                                <div {...defns.detailContainer}>
                                    <SchoolIcon {...defns.icons} />
                                    <AfitdTypography {...defns.detail}>{userProfileInfo?.education}</AfitdTypography>
                                </div>
                                {_map(userProfileInfo?.otherEducation, otherEducation => (
                                    <div {...defns.detailContainer} key={getUniqueKey()}>
                                        <SchoolIcon {...defns.icons} />
                                        <AfitdTypography {...defns.detail}>{otherEducation}</AfitdTypography>
                                    </div>
                                ))}
                            </div>

                            <Grid {...defns.employerActionsContainer}>
                                <Grid item xl={8} lg={8} md={8} sm={11} xs={11}>
                                    <AfitdButton {...defns.interviewButton}>{mdMatchesOnly ? 'Interview' : 'Request Interview'} </AfitdButton>
                                    <AfitdButton {...defns.messageButton}> {mdMatchesOnly ? 'Message' : 'Send Message'} </AfitdButton>
                                </Grid>
                                <Grid item xl={4} lg={4} md={4} sm={11} xs={11}>
                                    <AfitdButton {...defns.saveToJobsButton}>{isJobSeekerAlreadySaved ? 'Saved to My Jobs' : 'Save to My Jobs'} </AfitdButton>
                                    <SaveJobSeekerToMyJobsPopover {...defns.saveJobSeekerToMyJobsPopover} />
                                </Grid>
                            </Grid>

                            <AfitdTypography {...defns.yearsOfExperienceTitleText}>Years of Experience:</AfitdTypography>
                            <AfitdTypography {...defns.descriptionText}>{getYearsOfExperienceText(userProfileInfo?.yearsOfExperience)}</AfitdTypography>

                            <WillingToRelocate {...defns.willingRelocate} />

                            <AfitdTypography {...defns.aboutMeTitleText}>About Me:</AfitdTypography>
                            <AfitdTypography {...defns.descriptionText}>{userProfileInfo?.aboutMe}</AfitdTypography>

                            <AfitdTypography {...defns.occupationTitleText}>Desired Occupation:</AfitdTypography>
                            <div {...defns.chipsContainer}>
                                {userProfileInfo?.occupation?.map((occupation, idx) => (
                                    <AfitdTypography key={getUniqueKey()} {...defns.occupations}>
                                        {occupation}
                                    </AfitdTypography>
                                ))}
                            </div>

                            <AfitdTypography {...defns.skillsetTitleText}>Skillset:</AfitdTypography>
                            <div {...defns.chipsContainer}>
                                {userProfileInfo?.skillset?.map((skills, idx) => (
                                    <AfitdTypography key={getUniqueKey()} {...defns.skills}>
                                        {skills}
                                    </AfitdTypography>
                                ))}
                            </div>

                            <AfitdTypography {...defns.videoOverviewTitleText}>Video Overview:</AfitdTypography>
                            <div {...defns.innerContainerVideoOverview}>
                                <div {...defns.existingVideoInfoContainer}>
                                    <div {...defns.existingVideoNameSizeContainer}>
                                        <AfitdTypography {...defns.existingVideoFilenameText} />
                                        <AfitdTypography {...defns.existingVideoFileSizeText} />
                                    </div>
                                    <div>
                                        <AfitdTypography {...defns.existingVideoDurationText} />
                                    </div>
                                </div>
                                <VideoOverviewBlock {...defns.videoOverviewBlock} userAvatar={userProfileInfo?.profilePicUrl} />
                            </div>

                            <AfitdTypography {...defns.resumeBlockTitleText}>Resume:</AfitdTypography>
                            <div {...defns.resumeContainer}>
                                <a href="https://www.google.com/" target="_blank" rel="noopener noreferrer" {...defns.resumeLink}>
                                    <div {...defns.inline}>
                                        <AfitdFileTypeIcon {...defns.resumeFileTypeIcon} />
                                        <div {...defns.fileTextContainer}>
                                            <AfitdTypography {...defns.resumeTitleText} />
                                            <AfitdTypography {...defns.resumeDateText} />
                                        </div>
                                    </div>
                                </a>
                            </div>

                            <AfitdTypography {...defns.socialMediaPortfolioBlockTitleText}>Social Media and Portfolio:</AfitdTypography>
                            {!_isEmpty(userProfileInfo.facebookUrl) && (
                                <div {...defns.socialMediaBlock}>
                                    <div {...defns.iconsContainer}>
                                        <FacebookIcon {...defns.socialIcons} />
                                    </div>
                                    <div>
                                        <AfitdTypography {...defns.socialMediaBlockTitle}>{PROFILE_SOCIAL_MEDIA_FACEBOOK}</AfitdTypography>
                                        <UrlLink {...defns.facebookUrl} />
                                    </div>
                                </div>
                            )}
                            {!_isEmpty(userProfileInfo.linkedInUrl) && (
                                <div {...defns.socialMediaBlock}>
                                    <div {...defns.iconsContainer}>
                                        <LinkedInIcon {...defns.socialIcons} />
                                    </div>
                                    <div>
                                        <AfitdTypography {...defns.socialMediaBlockTitle}>{PROFILE_SOCIAL_MEDIA_LINKEDIN}</AfitdTypography>
                                        <UrlLink {...defns.linkedInUrl} />
                                    </div>
                                </div>
                            )}
                            {!_isEmpty(userProfileInfo.instagramUrl) && (
                                <div {...defns.socialMediaBlock}>
                                    <div {...defns.iconsContainer}>
                                        <InstagramIcon {...defns.socialIcons} />
                                    </div>
                                    <div>
                                        <AfitdTypography {...defns.socialMediaBlockTitle}>{PROFILE_SOCIAL_MEDIA_INSTAGRAM}</AfitdTypography>
                                        <UrlLink {...defns.instagramUrl} />
                                    </div>
                                </div>
                            )}
                            {!_isEmpty(userProfileInfo.twitterUrl) && (
                                <div {...defns.socialMediaBlock}>
                                    <div {...defns.iconsContainer}>
                                        &nbsp;&nbsp;&nbsp; <img src={XIcon} alt="x_icon" width="28px" style={{ marginTop: '10px', marginLeft: 6 }} /> &nbsp;&nbsp;
                                    </div>
                                    <div>
                                        <AfitdTypography {...defns.socialMediaBlockTitleX}>{PROFILE_SOCIAL_MEDIA_X}</AfitdTypography>
                                        <UrlLink {...defns.xUrl} />
                                    </div>
                                </div>
                            )}
                            {!_isEmpty(userProfileInfo.youTubeUrl) && (
                                <div {...defns.socialMediaBlock}>
                                    <div {...defns.iconsContainer}>
                                        <YouTubeIcon {...defns.socialIcons} />
                                    </div>
                                    <div>
                                        <AfitdTypography {...defns.socialMediaBlockTitle}>{PROFILE_SOCIAL_MEDIA_YOUTUBE}</AfitdTypography>
                                        <UrlLink {...defns.youTubeUrl} />
                                    </div>
                                </div>
                            )}
                            {!_isEmpty(userProfileInfo.tikTokUrl) && (
                                <div {...defns.socialMediaBlock}>
                                    <div {...defns.iconsContainer}>
                                        &nbsp;&nbsp;&nbsp; <img src={TiktokIcon} alt="tiktok_icon" width="37px" style={{ marginTop: '10px' }} /> &nbsp;&nbsp;
                                    </div>
                                    <div>
                                        <AfitdTypography {...defns.socialMediaBlockTitleTikTok}>{PROFILE_SOCIAL_MEDIA_TIKTOK}</AfitdTypography>
                                        <UrlLink {...defns.tikTokUrl} />
                                    </div>
                                </div>
                            )}
                            {!_isEmpty(userProfileInfo.gitHubUrl) && (
                                <div {...defns.socialMediaBlock}>
                                    <div {...defns.iconsContainer}>
                                        <GitHubIcon {...defns.socialIcons} />
                                    </div>
                                    <div>
                                        <AfitdTypography {...defns.socialMediaBlockTitle}>{PROFILE_SOCIAL_MEDIA_GITHUB}</AfitdTypography>
                                        <UrlLink {...defns.gitHubUrl} />
                                    </div>
                                </div>
                            )}
                            {!_isEmpty(userProfileInfo.websiteUrl) && (
                                <div {...defns.socialMediaBlock}>
                                    <div {...defns.iconsContainer}>
                                        <WebsiteIcon {...defns.socialIcons} />
                                    </div>
                                    <div>
                                        <AfitdTypography {...defns.socialMediaBlockTitle}>{PROFILE_SOCIAL_MEDIA_WEBSITE}</AfitdTypography>
                                        <UrlLink {...defns.websiteUrl} />
                                    </div>
                                </div>
                            )}
                            <br />
                        </SimpleBar>
                    </div>
                </Grid>
            </Grid>
            <AfitdConfirmDialog {...defns.noActiveJobsDialog} />
            {interviewDialogOpen && <InterviewDialog {...defns.interviewDialog} />}
            {sendMessageDialogOpen && <SendMessageDialog {...defns.sendMessageDialog} />}
        </div>
    );
};

export default JobSeekerProfile;
